import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { loginClient, loginLawyer } from '../../services/api';

// Cargar datos persistidos
const persistedUser = JSON.parse(localStorage.getItem('user'));
const persistedToken = localStorage.getItem('token');

// Estados de la autenticación
const INITIAL_STATE = {
  user: persistedUser || null,
  token: persistedToken || null,
  userType: persistedUser?.type || null, // 'client' | 'lawyer'
  currentMode: 'client', // Modo actual en las páginas de auth: 'client' | 'lawyer'
  isAuthenticated: Boolean(persistedToken),
  isLoading: false,
  error: null,
  loginAttempts: 0,
  lastLoginAttempt: null,
};

// Async thunks para operaciones asíncronas
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ email, password, userType }, { rejectWithValue }) => {
    try {
      const response =
        userType === 'lawyer'
          ? await loginLawyer(email, password)
          : await loginClient(email, password);

      if (response.status === 0 || response.status === -3) {
        return {
          user: {
            id: response.id,
            name: response.name,
            email: email,
            logg_express: response.logg_express,
          },
          userType,
          token: response.token || response.logg_express, // Usar token si está disponible
        };
      } else {
        return rejectWithValue(response.message || 'Error en el login');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Error de conexión');
    }
  }
);

export const logoutUser = createAsyncThunk('auth/logoutUser', async (_, { dispatch }) => {
  // Aquí puedes agregar lógica para invalidar tokens en el servidor
  localStorage.removeItem('user');
  localStorage.removeItem('token');

  // Limpiar otros slices si es necesario
  dispatch({ type: 'forms/clearAllForms' });
  dispatch({ type: 'api/clearCache' });

  return null;
});

export const refreshToken = createAsyncThunk(
  'auth/refreshToken',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (!auth.token) {
        throw new Error('No token available');
      }

      // Aquí implementarías la lógica de refresh token con tu API
      // const response = await refreshUserToken(auth.token);

      // Por ahora, simplemente validar que el token sigue siendo válido
      return { token: auth.token };
    } catch (error) {
      return rejectWithValue('Session expired');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState: INITIAL_STATE,
  reducers: {
    // Acciones síncronas
    clearError: state => {
      state.error = null;
    },

    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = true;
    },

    updateUserProfile: (state, action) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },

    resetLoginAttempts: state => {
      state.loginAttempts = 0;
      state.lastLoginAttempt = null;
    },

    setCurrentMode: (state, action) => {
      state.currentMode = action.payload; // 'client' | 'lawyer'
    },

    // Acción para restaurar estado desde localStorage (hydration)
    restoreAuth: (state, action) => {
      const userData = action.payload;
      if (userData) {
        state.user = userData;
        state.userType = userData.type;
        state.isAuthenticated = true;
        state.token = userData.logg_express;
      }
    },
  },

  extraReducers: builder => {
    // Login User
    builder
      .addCase(loginUser.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.userType = action.payload.userType;
        state.token = action.payload.token;
        state.isAuthenticated = true;

        // Guardar token
        localStorage.setItem('token', action.payload.token);
      })

      // Logout User
      .addCase(logoutUser.fulfilled, state => {
        // Limpiar persistencia
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        return INITIAL_STATE;
      });
  },
});

// Export actions
export const {
  clearError,
  setUser,
  updateUserProfile,
  resetLoginAttempts,
  restoreAuth,
  setCurrentMode,
} = authSlice.actions;

// Selectors
export const selectAuth = state => state.auth;
export const selectUser = state => state.auth.user;
export const selectIsAuthenticated = state => state.auth.isAuthenticated;
export const selectUserType = state => state.auth.userType;
export const selectCurrentMode = state => state.auth.currentMode;
export const selectIsLawyer = state => state.auth.currentMode === 'lawyer';
export const selectIsLoading = state => state.auth.isLoading;
export const selectAuthError = state => state.auth.error;
export const selectCanAttemptLogin = state => {
  const { loginAttempts, lastLoginAttempt } = state.auth;

  // Permitir máximo 5 intentos
  if (loginAttempts >= 5) {
    // Si han pasado más de 15 minutos, permitir intentar de nuevo
    if (lastLoginAttempt) {
      const timeDiff = new Date() - new Date(lastLoginAttempt);
      return timeDiff > 15 * 60 * 1000; // 15 minutos
    }
    return false;
  }
  return true;
};

export default authSlice.reducer;

// En App.js o componente raíz se dispararía:
// if (INITIAL_STATE.token) {
//   store.dispatch(refreshToken());
// }
